/**
 * LocationAutocomplete — thin autocomplete over
 * /api/newspulse/v1/local/geocode (Nominatim). Emits
 * `onSelect({ city, state, country, lat, lon })` so the parent form
 * can auto-fill the location fields from one lookup.
 */
import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { npApi } from "@/pages/newspulse/api";

export default function LocationAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Type a city or village…",
  testId = "location-autocomplete",
}) {
  const [q, setQ] = useState(value);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);

  useEffect(() => setQ(value), [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const d = await npApi.localGeocode(q, 6);
        setResults(Array.isArray(d) ? d : (d.items || []));
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="relative" ref={wrap} data-testid={testId}>
      <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 focus-within:border-blue-500">
        <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); onChange && onChange(e.target.value); }}
          onFocus={() => q.length >= 2 && setOpen(true)}
          placeholder={placeholder}
          className="flex-1 text-sm bg-transparent border-0 outline-none"
          data-testid={`${testId}-input`}
        />
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto"
            data-testid={`${testId}-results`}>
          {results.map((r, i) => (
            <li key={`${r.name}-${r.lat}-${i}`}>
              <button
                type="button"
                onClick={() => {
                  const picked = {
                    city: r.name, state: r.admin1 || "",
                    country: r.country_name || r.country || "",
                    lat: r.lat, lon: r.lon,
                  };
                  setQ(picked.city);
                  setOpen(false);
                  onSelect && onSelect(picked);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                data-testid={`${testId}-item-${i}`}
              >
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="flex-1">
                  <span className="font-medium text-slate-900">{r.name}</span>
                  {(r.admin1 || r.country_name) && (
                    <span className="text-slate-500"> · {[r.admin1, r.country_name].filter(Boolean).join(", ")}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
